import { useNavigate, useParams } from "react-router-dom";
import { Archive, ArchiveRestore, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useArchiveSubscriber, useSubscriber, useUpdateSubscriber } from "@/hooks/queries";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge } from "@/components/ui/Badge";
import { cn, formatDate } from "@/lib/utils";
import { InfoTab } from "./tabs/Info";
import { RepresentativesTab } from "./tabs/Representatives";
import { CertificateRequestsTab } from "./tabs/CertificateRequests";
import { ArtifactsTab } from "./tabs/Artifacts";
import { GenerateTab } from "./tabs/Generate";
import { MailboxTab } from "./tabs/Mailbox";
import { AndesTab } from "./tabs/Andes";
import { ApiError } from "@/lib/api";

const TABS = [
  { key: "info", label: "Info" },
  { key: "representatives", label: "Representatives" },
  { key: "certificate-requests", label: "Certificate Requests" },
  { key: "artifacts", label: "Artifacts" },
  { key: "generate", label: "Generate" },
  { key: "mailbox", label: "Buzón" },
  { key: "andes", label: "Andes" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export function SubscriberDetail() {
  const params = useParams<{ id: string; tab?: string }>();
  const id = params.id!;
  const currentTab = (TABS.find((t) => t.key === params.tab)?.key ?? "info") as TabKey;
  const navigate = useNavigate();
  const subscriber = useSubscriber(id);
  const archive = useArchiveSubscriber();
  const update = useUpdateSubscriber(id);

  async function onArchive() {
    if (
      !confirm(
        "Archive this subscriber? It disappears from the active list and can't emit certificates, but all its data (representatives, requests, artifacts) is kept. You can restore it later."
      )
    )
      return;
    try {
      await archive.mutateAsync(id);
      toast.success("Subscriber archived");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to archive");
    }
  }

  async function onRestore() {
    try {
      await update.mutateAsync({ status: "active" });
      toast.success("Subscriber restored");
    } catch (err) {
      if (err instanceof ApiError) toast.error(`[${err.code}] ${err.message}`);
      else toast.error("Failed to restore");
    }
  }

  if (subscriber.isPending) return <CenteredSpinner label="Loading subscriber…" />;
  if (subscriber.isError)
    return <ErrorState error={subscriber.error} onRetry={() => subscriber.refetch()} />;

  const s = subscriber.data;
  const isJuridica = s.type === "juridica";

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/subscribers")}>
          <ArrowLeft className="h-4 w-4" /> Back to list
        </Button>
      </div>

      <Card>
        <CardContent className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-semibold text-slate-900 truncate">{s.name}</h1>
              <Badge tone={isJuridica ? "brand" : "info"}>{s.type}</Badge>
              {s.status === "archived" && <Badge tone="warning">Archived</Badge>}
            </div>
            <p className="text-xs text-slate-500 font-mono mt-1">{s.id}</p>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-slate-400">Document</p>
                <p className="text-slate-900">
                  {s.documentType} {s.documentNumber}
                  {s.verificationDigit ? `-${s.verificationDigit}` : ""}
                </p>
              </div>
              <div>
                <p className="text-slate-400">Email</p>
                <p className="text-slate-900 truncate">{s.email}</p>
              </div>
              <div>
                <p className="text-slate-400">Location</p>
                <p className="text-slate-900">
                  {s.city}, {s.state}, {s.country}
                </p>
              </div>
              <div>
                <p className="text-slate-400">Created</p>
                <p className="text-slate-900">{formatDate(s.createdAt)}</p>
              </div>
            </div>
          </div>
          {s.status === "archived" ? (
            <Button variant="outline" size="sm" onClick={onRestore} loading={update.isPending}>
              <ArchiveRestore className="h-3.5 w-3.5" /> Restore
            </Button>
          ) : (
            <Button variant="danger" size="sm" onClick={onArchive} loading={archive.isPending}>
              <Archive className="h-3.5 w-3.5" /> Archive
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="border-b border-slate-200">
        <nav className="flex gap-1">
          {TABS.map((t) => {
            if (t.key === "representatives" && !isJuridica) return null;
            return (
              <button
                key={t.key}
                onClick={() =>
                  navigate(`/subscribers/${id}${t.key === "info" ? "" : `/${t.key}`}`)
                }
                className={cn(
                  "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                  currentTab === t.key
                    ? "border-brand-600 text-brand-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                )}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div>
        {currentTab === "info" && <InfoTab subscriber={s} />}
        {currentTab === "representatives" && isJuridica && <RepresentativesTab subscriberId={id} />}
        {currentTab === "certificate-requests" && (
          <CertificateRequestsTab
            subscriberId={id}
            documentNumber={s.documentNumber}
            subscriberType={s.type}
          />
        )}
        {currentTab === "artifacts" && <ArtifactsTab subscriberId={id} />}
        {currentTab === "generate" && <GenerateTab subscriberId={id} />}
        {currentTab === "mailbox" && <MailboxTab subscriber={s} />}
        {currentTab === "andes" && <AndesTab subscriberId={id} />}
      </div>
    </div>
  );
}
